import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Check, Pencil } from "lucide-react";
import { api, extractApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth-context";
import { canCreateOrEditCRM } from "../lib/crm-permissions";
import { ASSIGNEE_UNASSIGNED, getAssigneeLabel, getTaskAssigneeLabel, toAssignedUserId } from "../lib/tasks-assignees";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import StatusPill from "../components/status/StatusPill";
import EmptyState from "../components/empty/EmptyState";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { TASK_STATUS, PRIORITY } from "../lib/enums";
import { TASK_STATUS_LABELS, PRIORITY_LABELS } from "../lib/labels";
import { formatDate } from "../lib/format";
import { getTaskPrimaryRelation, isTaskOverdue } from "../lib/task-detail";

const ALL = "__all__";
const INITIAL = {
  title: "",
  description: "",
  priority: PRIORITY.MEDIUM,
  status: TASK_STATUS.OPEN,
  due_date: "",
  assigned_user_id: ASSIGNEE_UNASSIGNED,
};

const REL_NONE = "none";
const REL_LEAD = "lead";
const REL_COMPANY = "company";
const REL_CONTACT = "contact";
const REL_DEAL = "deal";

export default function TasksPage() {
  const navigate = useNavigate();
  const { role, isSuperAdmin, user } = useAuth();
  const perm = { role, isSuperAdmin };
  const canWrite = canCreateOrEditCRM(perm);
  const canToggleTaskCompletion =
    role === "TENANT_ADMIN" || role === "SALES_USER" || Boolean(isSuperAdmin);
  const userId = user?.id || null;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: ALL, priority: ALL });
  const [toggles, setToggles] = useState({ overdue: false, mine: false });
  const [search, setSearch] = useState("");
  const [createDialog, setCreateDialog] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(INITIAL);
  const [editSaving, setEditSaving] = useState(false);

  const [editRelType, setEditRelType] = useState(REL_NONE);
  const [editRelId, setEditRelId] = useState("");
  const [editRelInitial, setEditRelInitial] = useState({ type: REL_NONE, id: "" });
  const [relQuery, setRelQuery] = useState("");
  const [relItems, setRelItems] = useState([]);
  const [relLoading, setRelLoading] = useState(false);
  const [relError, setRelError] = useState("");
  const [relCurrentItem, setRelCurrentItem] = useState(null);
  const [relCurrentLoading, setRelCurrentLoading] = useState(false);
  const [relCurrentError, setRelCurrentError] = useState("");

  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneesError, setAssigneesError] = useState("");

  const assigneesById = useMemo(() => {
    const map = {};
    for (const u of assignees || []) {
      if (u?.id) map[u.id] = u;
    }
    return map;
  }, [assignees]);

  const assigneeOptions = useMemo(() => {
    const items = (assignees || [])
      .filter((u) => u?.id)
      .map((u) => ({ id: u.id, label: getAssigneeLabel(u) }));
    items.sort((a, b) => a.label.localeCompare(b.label, "de"));
    return items;
  }, [assignees]);

  const searchRef = useRef(search);
  const filtersRef = useRef(filters);
  const togglesRef = useRef(toggles);
  const userIdRef = useRef(userId);
  searchRef.current = search;
  filtersRef.current = filters;
  togglesRef.current = toggles;
  userIdRef.current = userId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchRef.current) params.search = searchRef.current;
      if (filtersRef.current.status !== ALL) params.status = filtersRef.current.status;
      if (filtersRef.current.priority !== ALL) params.priority = filtersRef.current.priority;
      if (togglesRef.current.overdue) params.overdue = true;
      if (togglesRef.current.mine && userIdRef.current) params.assigned_user_id = userIdRef.current;
      const r = await api.get("/api/tasks", { params });
      setRows(r.data);
    } catch (e) {
      toast.error(extractApiError(e, "Aufgaben konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [filters.priority, filters.status, toggles.overdue, toggles.mine, load]);

  const loadAssignees = useCallback(async () => {
    setAssigneesLoading(true);
    setAssigneesError("");
    try {
      const r = await api.get("/api/settings/users");
      setAssignees(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setAssignees([]);
      setAssigneesError(extractApiError(e, "Teammitglieder konnten nicht geladen werden."));
    } finally {
      setAssigneesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignees();
  }, [loadAssignees]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Bitte einen Titel angeben.");
    setSaving(true);
    try {
      const assignedUserId = toAssignedUserId(form.assigned_user_id);
      const payload = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        status: form.status,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        ...(assignedUserId ? { assigned_user_id: assignedUserId } : {}),
      };
      await api.post("/api/tasks", payload);
      toast.success("Aufgabe erstellt.");
      setForm(INITIAL);
      setCreateDialog(false);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Aufgabe konnte nicht erstellt werden."));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (event, task, newStatus) => {
    event?.stopPropagation?.();
    let previousTask = null;
    setRows((current) =>
      current.map((row) => {
        if (row.id !== task.id) return row;
        previousTask = row;
        return { ...row, status: newStatus };
      })
    );
    try {
      await api.patch(`/api/tasks/${task.id}`, { status: newStatus });
      toast.success("Aufgabe aktualisiert.");
    } catch (e) {
      if (previousTask) {
        setRows((current) =>
          current.map((row) => (row.id === task.id ? previousTask : row))
        );
      }
      toast.error(extractApiError(e, "Aktualisierung fehlgeschlagen."));
    }
  };

  const openEdit = (task) => {
    const rel = getTaskPrimaryRelation(task);
    setEditing(task);
    setEditForm({
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || PRIORITY.MEDIUM,
      status: task.status || TASK_STATUS.OPEN,
      due_date: isoToDateInput(task.due_date),
      assigned_user_id: task.assigned_user_id || ASSIGNEE_UNASSIGNED,
    });
    setEditRelType(rel.type);
    setEditRelId(rel.id || "");
    setEditRelInitial(rel);
    setRelQuery("");
    setRelItems([]);
    setRelLoading(false);
    setRelError("");
    setRelCurrentItem(null);
    setRelCurrentLoading(false);
    setRelCurrentError("");
    setEditDialog(true);
  };

  useEffect(() => {
    const endpoint = getRelDetailEndpoint(editRelType, editRelId);
    if (!endpoint) {
      setRelCurrentItem(null);
      setRelCurrentLoading(false);
      setRelCurrentError("");
      return;
    }
    let cancelled = false;
    (async () => {
      setRelCurrentLoading(true);
      setRelCurrentError("");
      try {
        const r = await api.get(endpoint);
        if (cancelled) return;
        setRelCurrentItem(r.data || null);
      } catch (e) {
        if (cancelled) return;
        setRelCurrentItem(null);
        setRelCurrentError(extractApiError(e, "Verknüpftes Objekt konnte nicht geladen werden."));
      } finally {
        if (!cancelled) setRelCurrentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editRelType, editRelId]);

  useEffect(() => {
    const endpoint = getRelListEndpoint(editRelType);
    if (!endpoint) {
      setRelItems([]);
      setRelLoading(false);
      setRelError("");
      return;
    }
    if ((relQuery || "").trim().length < 2) {
      setRelItems([]);
      setRelLoading(false);
      setRelError("");
      return;
    }
    let cancelled = false;
    (async () => {
      setRelLoading(true);
      setRelError("");
      try {
        const r = await api.get(endpoint, { params: { search: relQuery.trim() } });
        if (cancelled) return;
        setRelItems(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (cancelled) return;
        setRelItems([]);
        setRelError(extractApiError(e, "Suche fehlgeschlagen."));
      } finally {
        if (!cancelled) setRelLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editRelType, relQuery]);

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    if (!editForm.title.trim()) return toast.error("Bitte einen Titel angeben.");
    setEditSaving(true);
    try {
      const payload = {};
      if ((editing.title || "") !== editForm.title) payload.title = editForm.title;
      if ((editing.description || "") !== editForm.description) payload.description = editForm.description;
      if ((editing.priority || PRIORITY.MEDIUM) !== editForm.priority) payload.priority = editForm.priority;
      if ((editing.status || TASK_STATUS.OPEN) !== editForm.status) payload.status = editForm.status;
      const dueIso = dateInputToIso(editForm.due_date);
      const prevDueIso = editing.due_date ? new Date(editing.due_date).toISOString() : null;
      if (prevDueIso !== dueIso) payload.due_date = dueIso;
      const prevAssigned = editing.assigned_user_id || null;
      const nextAssigned = toAssignedUserId(editForm.assigned_user_id);
      if (prevAssigned !== nextAssigned) payload.assigned_user_id = nextAssigned;
      const relDirty = (editRelInitial?.type || REL_NONE) !== (editRelType || REL_NONE) || (editRelInitial?.id || "") !== (editRelId || "");
      if (relDirty) {
        if ((editRelType === REL_LEAD || editRelType === REL_COMPANY || editRelType === REL_CONTACT) && !editRelId) {
          toast.error("Bitte eine Verknüpfung auswählen oder „Nicht verknüpft“ wählen.");
          return;
        }
        const relPatch = buildRelationPatch(editRelType, editRelId);
        if (relPatch) Object.assign(payload, relPatch);
      }
      if (Object.keys(payload).length === 0) {
        setEditDialog(false);
        return;
      }
      await api.patch(`/api/tasks/${editing.id}`, payload);
      toast.success("Aufgabe aktualisiert.");
      setEditDialog(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Aufgabe konnte nicht aktualisiert werden."));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Aufgaben"
        description="Follow-ups, To-Dos und Termine verwalten."
        actions={
          canWrite ? (
            <Button onClick={() => setCreateDialog(true)} data-testid="task-create-button">
              <Plus className="w-4 h-4 mr-1.5" /> Aufgabe erstellen
            </Button>
          ) : null
        }
      />

      <div className="surface rounded-xl p-3 mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="tasks-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suche …" className="pl-11" />
        </form>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger data-testid="tasks-filter-status" className="md:w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Status: Alle</SelectItem>
            {Object.values(TASK_STATUS).map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s].de}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
          <SelectTrigger data-testid="tasks-filter-priority" className="md:w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Priorität: Alle</SelectItem>
            {Object.values(PRIORITY).map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p].de}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="soft"
            onClick={() => setToggles((t) => ({ ...t, overdue: !t.overdue }))}
            className={toggles.overdue ? "border-brand/50 bg-brand/20 text-brand shadow-sm" : ""}
            data-testid="tasks-filter-overdue"
          >
            Überfällig
          </Button>
          {userId ? (
            <Button
              type="button"
              size="sm"
              variant="soft"
              onClick={() => setToggles((t) => ({ ...t, mine: !t.mine }))}
              className={toggles.mine ? "border-brand/50 bg-brand/20 text-brand shadow-sm" : ""}
              data-testid="tasks-filter-mine"
            >
              Meine Aufgaben
            </Button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Wird geladen …</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Keine Aufgaben"
          description={canWrite ? "Erstellen Sie Ihre erste Aufgabe oder passen Sie Filter an." : "Keine Aufgaben gefunden. Passen Sie Filter an."}
          actionLabel={canWrite ? "Aufgabe erstellen" : null}
          onAction={canWrite ? () => setCreateDialog(true) : null}
          testId="tasks-empty"
        />
      ) : (
        <div data-testid="tasks-list" className="surface rounded-xl divide-y divide-border/60">
          {rows.map((t) => {
            const st = TASK_STATUS_LABELS[t.status] || { de: t.status, tone: "muted" };
            const pr = PRIORITY_LABELS[t.priority] || { de: t.priority, tone: "muted" };
            const done = t.status === TASK_STATUS.DONE || t.status === TASK_STATUS.CANCELLED;
            const overdue = isTaskOverdue(t.due_date, t.status);
            const assigneeLabel = t.assigned_user_id
              ? getTaskAssigneeLabel({ assignedUserId: t.assigned_user_id, usersById: assigneesById })
              : "";
            return (
              <div
                key={t.id}
                data-testid={`task-row-${t.id}`}
                className="p-3 flex flex-col items-stretch gap-3 sm:flex-row sm:items-start"
              >
                <button
                  type="button"
                  data-testid={`task-toggle-${t.id}`}
                  onClick={(event) => toggleStatus(event, t, done ? TASK_STATUS.OPEN : TASK_STATUS.DONE)}
                  disabled={!canToggleTaskCompletion}
                  className={`relative z-10 h-9 w-9 shrink-0 self-start rounded-xl border inline-flex items-center justify-center transition-colors ${
                    done
                      ? "border-brand/50 bg-brand/20 text-brand shadow-sm"
                      : "border-brand/25 bg-brand/5 text-transparent hover:border-brand/40 hover:bg-brand/10"
                  }`}
                  aria-label={done ? "Öffnen" : "Erledigt markieren"}
                >
                  <Check className="w-3 h-3" />
                </button>
                <div data-testid={`task-content-${t.id}`} className="w-full flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/aufgaben/${t.id}`)}
                    className="block w-full text-left"
                    data-testid={`task-open-${t.id}`}
                  >
                    <div
                      data-testid={`task-title-${t.id}`}
                      className={`text-sm break-words hyphens-auto [overflow-wrap:anywhere] ${done ? "line-through text-muted-foreground" : "font-medium hover:underline"}`}
                      lang="de"
                    >
                      {t.title}
                    </div>
                    {t.description ? (
                      <div
                        className="text-xs text-muted-foreground break-words hyphens-auto [overflow-wrap:anywhere]"
                        lang="de"
                      >
                        {t.description}
                      </div>
                    ) : null}
                    {t.assigned_user_id ? (
                      <div className="text-xs text-muted-foreground">Zugewiesen an: {assigneeLabel}</div>
                    ) : null}
                  </button>
                </div>
                <div
                  data-testid={`task-meta-${t.id}`}
                  className="flex w-full flex-wrap items-center gap-2 text-xs sm:ml-auto sm:w-auto sm:justify-end"
                >
                  {t.due_date && (
                    <span className={overdue ? "text-destructive" : "text-muted-foreground"}>
                      Fällig {formatDate(t.due_date)}
                    </span>
                  )}
                  {overdue ? <StatusPill tone="destructive">Überfällig</StatusPill> : null}
                  <StatusPill tone={pr.tone}>{pr.de}</StatusPill>
                  <StatusPill tone={st.tone}>{st.de}</StatusPill>
                  {canWrite ? (
                    <Button type="button" size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`task-edit-${t.id}`}>
                      <Pencil />
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={createDialog} onOpenChange={(open) => { if (!saving) setCreateDialog(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Aufgabe erstellen</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <Fld label="Titel *"><Input data-testid="task-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus /></Fld>
            <Fld label="Beschreibung"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Fld>
            <div className="grid md:grid-cols-2 gap-3">
              <Fld label="Fälligkeitsdatum"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Fld>
              <Fld label="Zugewiesen an">
                <Select
                  value={form.assigned_user_id}
                  onValueChange={(v) => setForm({ ...form, assigned_user_id: v })}
                  disabled={assigneesLoading || Boolean(assigneesError)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ASSIGNEE_UNASSIGNED}>Nicht zugewiesen</SelectItem>
                    {assigneeOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {assigneesError ? <div className="text-xs text-muted-foreground mt-1">{assigneesError}</div> : null}
              </Fld>
              <Fld label="Priorität">
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(PRIORITY).map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p].de}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(TASK_STATUS).map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s].de}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialog(false)} disabled={saving}>Abbrechen</Button>
              <Button type="submit" disabled={saving} data-testid="task-form-submit">
                {saving ? "Speichern …" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editDialog}
        onOpenChange={(open) => {
          if (editSaving) return;
          setEditDialog(open);
          if (!open) setEditing(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Aufgabe bearbeiten</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3">
            <Fld label="Titel *"><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} autoFocus /></Fld>
            <Fld label="Beschreibung"><Textarea rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></Fld>
            <div className="grid md:grid-cols-2 gap-3">
              <Fld label="Fälligkeitsdatum"><Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} /></Fld>
              <Fld label="Zugewiesen an">
                <Select value={editForm.assigned_user_id} onValueChange={(v) => setEditForm({ ...editForm, assigned_user_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ASSIGNEE_UNASSIGNED}>Nicht zugewiesen</SelectItem>
                    {assigneesLoading ? <SelectItem value="__loading__" disabled>Wird geladen …</SelectItem> : null}
                    {!assigneesLoading &&
                      editForm.assigned_user_id &&
                      editForm.assigned_user_id !== ASSIGNEE_UNASSIGNED &&
                      !assigneesById[editForm.assigned_user_id] ? (
                        <SelectItem value={editForm.assigned_user_id}>Unbekannt</SelectItem>
                      ) : null}
                    {assigneeOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {assigneesError ? <div className="text-xs text-muted-foreground mt-1">{assigneesError}</div> : null}
              </Fld>
              <Fld label="Priorität">
                <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(PRIORITY).map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p].de}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Status">
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(TASK_STATUS).map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s].de}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Fld label="Verknüpft mit">
                <Select
                  value={editRelType}
                  onValueChange={(v) => {
                    setEditRelType(v);
                    setRelQuery("");
                    setRelItems([]);
                    setRelError("");
                    setRelCurrentError("");
                    setRelCurrentItem(null);
                    setEditRelId("");
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={REL_NONE}>Nicht verknüpft</SelectItem>
                    <SelectItem value={REL_LEAD}>Lead</SelectItem>
                    <SelectItem value={REL_COMPANY}>Firma</SelectItem>
                    <SelectItem value={REL_CONTACT}>Kontakt</SelectItem>
                    {editRelInitial?.type === REL_DEAL ? (
                      <SelectItem value={REL_DEAL} disabled>Deal (Bearbeitung folgt später)</SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>
                {editRelInitial?.type === REL_DEAL ? (
                  <div className="text-xs text-muted-foreground mt-1">Aktuell verknüpft mit: Deal</div>
                ) : null}
              </Fld>
              {editRelType === REL_LEAD || editRelType === REL_COMPANY || editRelType === REL_CONTACT ? (
                <Fld label={getRelSelectLabel(editRelType)}>
                  <Input value={relQuery} onChange={(e) => setRelQuery(e.target.value)} placeholder="Suchen …" />
                  <div className="mt-2">
                    <Select value={editRelId || ""} onValueChange={(v) => setEditRelId(v)}>
                      <SelectTrigger><SelectValue placeholder="Bitte auswählen" /></SelectTrigger>
                      <SelectContent>
                        {relCurrentLoading ? <SelectItem value="__loading__" disabled>Wird geladen …</SelectItem> : null}
                        {editRelId && !isRelIdInItems(editRelType, editRelId, relItems) ? (
                          <SelectItem value={editRelId}>{formatRelOption(editRelType, relCurrentItem, editRelId)}</SelectItem>
                        ) : null}
                        {relItems.map((it) => (
                          <SelectItem key={it.id} value={it.id}>
                            {formatRelOption(editRelType, it, it.id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {relCurrentError ? <div className="text-xs text-muted-foreground mt-1">{relCurrentError}</div> : null}
                    {(relQuery || "").trim().length < 2 ? (
                      <div className="text-xs text-muted-foreground mt-1">Bitte mindestens 2 Zeichen eingeben, um zu suchen.</div>
                    ) : relError ? (
                      <div className="text-xs text-muted-foreground mt-1">{relError}</div>
                    ) : !relLoading && relItems.length === 0 ? (
                      <div className="text-xs text-muted-foreground mt-1">Keine passenden Einträge gefunden.</div>
                    ) : null}
                  </div>
                </Fld>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialog(false)} disabled={editSaving}>Abbrechen</Button>
              <Button type="submit" disabled={editSaving}>{editSaving ? "Speichern …" : "Speichern"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function isoToDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateInputToIso(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function getRelListEndpoint(type) {
  if (type === REL_LEAD) return "/api/leads";
  if (type === REL_COMPANY) return "/api/companies";
  if (type === REL_CONTACT) return "/api/contacts";
  return null;
}

function getRelDetailEndpoint(type, id) {
  if (!id) return null;
  if (type === REL_LEAD) return `/api/leads/${id}`;
  if (type === REL_COMPANY) return `/api/companies/${id}`;
  if (type === REL_CONTACT) return `/api/contacts/${id}`;
  if (type === REL_DEAL) return `/api/deals/${id}`;
  return null;
}

function getRelSelectLabel(type) {
  if (type === REL_LEAD) return "Lead auswählen";
  if (type === REL_COMPANY) return "Firma auswählen";
  if (type === REL_CONTACT) return "Kontakt auswählen";
  return "Auswählen";
}

function buildRelationPatch(type, id) {
  if (type === REL_NONE) {
    return {
      related_lead_id: null,
      related_company_id: null,
      related_contact_id: null,
      related_deal_id: null,
    };
  }
  if (type === REL_LEAD) {
    return {
      related_lead_id: id,
      related_company_id: null,
      related_contact_id: null,
      related_deal_id: null,
    };
  }
  if (type === REL_COMPANY) {
    return {
      related_company_id: id,
      related_lead_id: null,
      related_contact_id: null,
      related_deal_id: null,
    };
  }
  if (type === REL_CONTACT) {
    return {
      related_contact_id: id,
      related_lead_id: null,
      related_company_id: null,
      related_deal_id: null,
    };
  }
  return null;
}

function isRelIdInItems(type, id, items) {
  if (!id) return false;
  if (!Array.isArray(items)) return false;
  return items.some((it) => it?.id === id);
}

function formatRelOption(type, item, fallbackId) {
  if (type === REL_LEAD) return formatLeadOption(item, fallbackId);
  if (type === REL_COMPANY) return formatCompanyOption(item, fallbackId);
  if (type === REL_CONTACT) return formatContactOption(item, fallbackId);
  return "Unbekannt";
}

function formatLeadOption(lead, fallbackId) {
  const title = lead?.title || "Unbekannt";
  const secondary = lead?.company_name || lead?.contact_email || "";
  const archived = lead?.archived_at ? " (archiviert)" : "";
  return (
    <div className="flex flex-col">
      <div>{title}{archived}</div>
      {secondary ? <div className="text-xs text-muted-foreground">{secondary}</div> : null}
      {!secondary && fallbackId ? <div className="text-xs text-muted-foreground">{fallbackId}</div> : null}
    </div>
  );
}

function formatCompanyOption(company, fallbackId) {
  const name = company?.name || "Unbekannt";
  const secondary = company?.industry || company?.website || "";
  const archived = company?.archived_at ? " (archiviert)" : "";
  return (
    <div className="flex flex-col">
      <div>{name}{archived}</div>
      {secondary ? <div className="text-xs text-muted-foreground">{secondary}</div> : null}
      {!secondary && fallbackId ? <div className="text-xs text-muted-foreground">{fallbackId}</div> : null}
    </div>
  );
}

function formatContactOption(contact, fallbackId) {
  const fullName = `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim();
  const email = contact?.email || "";
  const primary = fullName || email || "Unbekannt";
  const secondary = fullName && email ? email : "";
  const archived = contact?.archived_at ? " (archiviert)" : "";
  return (
    <div className="flex flex-col">
      <div>{primary}{archived}</div>
      {secondary ? <div className="text-xs text-muted-foreground">{secondary}</div> : null}
      {!secondary && fallbackId && primary === "Unbekannt" ? <div className="text-xs text-muted-foreground">{fallbackId}</div> : null}
    </div>
  );
}
